<template>
  <v-card class="v-card--flat">
    <v-row class="mb-3">
      <v-chip class="my-1 mr-3" color="primary">
        <h3>3/3</h3>
      </v-chip>
      <h2
        :style="`color: ${$vuetify.theme.themes.light.primary}`"
        class="pl-2 pa-sm-0"
      >
        {{ $translate('ENTER_YOUR_PERSONAL_DETAILS') }}
      </h2>
    </v-row>
    <p class="pl-2 pa-sm-0">
      {{ $translate('PERSONAL_DETAILS_EXPLANATION') }}
    </p>

    <!-- Error banner -->
    <v-slide-y-transition>
      <ErrorCard
        v-if="error"
        :errorMessage="errorMessage"
        :functionName="errorToRecallFunction"
        @book="book"
      />
    </v-slide-y-transition>

    <!-- User Detail rows -->
    <v-form v-model="valid" ref="form" lazy-validation>
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            :label="$translate('FIRSTNAME')"
            v-model="user.firstName"
            required
            :rules="[(v) => !!v || $translate('FIRST_NAME_REQUIRED')]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            :label="$translate('LASTNAME')"
            v-model="user.lastName"
            required
            :rules="[(v) => !!v || $translate('LAST_NAME_REQUIRED')]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            :label="$translate('EMAIL')"
            v-model="user.email"
            :disabled="$store.getters['auth/userName'] != ''"
            :rules="emailRules"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            :label="$translate('PHONE_NR')"
            v-model="user.phoneNo"
            required
            :rules="[(v) => !!v || $translate('PHONE_NR_REQUIRED')]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6" v-if="$store.getters['auth/userName'] == ''">
          <v-text-field
            :label="$translate('CONFIRM_EMAIL')"
            v-model="confirmEmail"
            :rules="confirmEmailRules"
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row
        v-if="
          orderData.OrderType.Type === 'Delivery' ||
          orderData.OrderType.Type === 'Shiplee'
        "
      >
        <v-col cols="12" md="6">
          <v-text-field
            :label="$translate('STREET')"
            v-model="user.street"
            required
            :rules="[(v) => !!v || $translate('STREET_REQUIRED')]"
            @change="geocodeAddress"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            :label="$translate('HOUSE_NUMBER')"
            v-model="user.houseNo"
            required
            :rules="[(v) => !!v || $translate('HOUSE_NR_REQUIRED')]"
            @change="geocodeAddress"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            :label="$translate('POSTAL_CODE')"
            v-model="user.postalCode"
            @change="getDeliveryFee"
            :rules="[(v) => !!v || $translate('POSTAL_CODE_REQUIRED')]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <p v-bind:class="{ 'error-text': deliveryError }">
            {{ deliveryText }}
          </p>
        </v-col>
      </v-row>

      <v-col cols="12" md="6">
        <v-textarea
          solo
          :label="$translate('REMARK_OPTIONAL')"
          v-model="remarks"
        ></v-textarea>
      </v-col>
    </v-form>

    <!-- Buttons -->
    <v-row class="mb-5">
      <v-btn color="secondary" dark @click="back()">
        {{ $translate('BACK') }}
      </v-btn>
      <v-spacer />
      <v-btn
        :disabled="deliveryError"
        @click="reserveOrderEvent()"
        color="primary"
        dark
        class="primary-font-color"
      >
        {{ $translate('BOOK') }}
      </v-btn>
    </v-row>

    <!-- Success dialog -->
    <SuccessDialog
      :dialog="successDialog"
      :successText="paymentMsg"
      @continue="goToMyActivities()"
      @back="goHome()"
    />
  </v-card>
</template>

<script>
import UserObject from '@/objects/UserObject';
import ErrorCard from '@/components/generalComponents/ErrorHandling/ErrorCard';
import doRequest from '@/api/dssRequest';
import forwardGeocoding from '@/api/Geocoding';
import SuccessDialog from '../generalComponents/uiComponents/SuccessDialog';

export default {
  name: 'BookingUserDetails',
  components: {
    ErrorCard,
    SuccessDialog,
  },
  props: {
    orderData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    eventDetails: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  created() {
    window.EventBus.listen('continueAfterErrorBookingInBookingUserDetails');
  },
  beforeDestroy() {
    window.EventBus.removeListener(
      'continueAfterErrorBookingInBookingUserDetails'
    );
  },
  data() {
    return {
      // ErrorCard variables
      error: false,
      errorMessage: this.$translate('BOOK_ORDER_FAILED'),
      errorToRecallFunction: 'book',

      productShoppingCart: [],
      order: this.$store.getters.getOrder,
      user: new UserObject(),
      remarks: '',
      successDialog: false,
      paymentMsg: '',
      confirmEmail: '',
      emailRules: [
        (v) => !!v || this.$translate('EMAIL_REQUIRED'),
        (v) => /.+@.+\..+/.test(v) || this.$translate('EMAIL_VALID'),
      ],
      confirmEmailRules: [
        (v) =>
          (!!v && v) === this.user.email || this.$translate('EMAIL_NO_MATCH'),
        (v) => /.+@.+\..+/.test(v) || this.$translate('EMAIL_VALID'),
      ],
      valid: null,

      deliveryText: '',
      deliveryError: null,
    };
  },
  computed: {
    userDetails() {
      return this.$store.getters['auth/user'];
    },
  },
  watch: {
    // to check if the user details are changed
    userDetails: {
      handler(userDetails) {
        this.user = userDetails;
      },
      deep: true,
    },
  },
  mounted() {
    // filter array items based on ShoppingCartType === 'Product' and put it in an array
    this.productShoppingCart = this.orderData.filter(
      (item) => item.ShoppingCartType === 'Product'
    );
    if (this.$store.getters['auth/userName'] !== '') {
      this.user = this.$store.getters['auth/user'];
    }
    if (this.user.postalCode && this.productShoppingCart.length !== 0) {
      this.getDeliveryFee();
    }
  },
  methods: {
    back() {
      console.log('triggered back');
      this.$emit('back');
    },
    goHome() {
      this.$router.push({
        name: 'Homepage',
        params: {
          account_id: this.$store.getters.getRegion,
        },
      });
    },
    goToMyActivities() {
      this.$router.push({
        name: 'UserSettings',
        params: { myOrders: true },
      });
    },
    getDeliveryFee() {
      const payload = {
        PostalCode: this.user.postalCode,
      };
      doRequest(
        (response) => {
          if (response.data.DeliveryPossible) {
            this.deliveryText = `${this.$translate(
              'DELIVERY_FEE'
            )}: €${this.roundPrice(response.data.Price)}`;
            this.deliveryError = false;
          } else {
            this.deliveryText = this.$translate('NO_DELIVERY_FOR_POSTAL_CODE');
            this.deliveryError = true;
          }
        },
        (error) => {
          this.deliveryText = this.$translate('NO_DELIVERY_FOR_POSTAL_CODE');
          this.deliveryError = true;
        },
        'getDeliveryFee',
        this.orderData.OrderType.EventId,
        payload
      );
    },
    roundPrice(price) {
      return Number(price).toFixed(2).replace('.', ',');
    },
    reserveOrderEvent() {
      const payload = {
        OrderId: '',
        EventId: this.orderData.OrderType.EventId,
        StartTime: this.orderData.StartTime.StartTime,
        EndTime: '',
        StartDate: this.orderData.StartDate,
        EndDate: this.orderData.EndDate,
        StartBatchGroupId: this.orderData.StartTime.BatchGroupId,
        EndBatchGroupId: this.orderData.StartTime.BatchGroupId,
        UserInfo: this.getUserInfo(),
      };
      payload.Note = '';
      payload.SpecialRequests = [];
      payload.Tickets = {};

      const object = this.$store.getters['auth/userName']
        ? this.$store.getters['auth/userName']
        : this.$store.getters['auth/tmpUserId'];

      doRequest(
        (response) => {
          const orderEvent = {
            EventId: response.data.EventInfo.EventId,
            OrderId: response.data.MainOrder.OrderId,
          };
          this.bookWithReservations(orderEvent, false);
        },
        (error) => {
          window.EventBus.fire('activateSnackBar', {
            text: this.$translate('FAILED_RESERVING_TICKETS'),
            color: 'error',
          });
        },
        'reserve',
        object,
        payload
      );
    },
    bookWithReservations(orderEvent, ignoreUserPlanboardBoolean) {
      this.error = false;
      const payload = {
        OnlySpecialRequests: false,
        IgnoreUserPlanBoard: ignoreUserPlanboardBoolean,
        UserInfo: this.getUserInfo(),
        Note: this.remarks,
      };
      const allEventsForOrder = {};
      this.$store.getters['shoppingCart/orders'].forEach((orderItem) => {
        // TODO make this much nicer!
        orderItem.MainOrder.Orders.forEach((indOrder) => {
          if (!allEventsForOrder.hasOwnProperty(indOrder.EventId)) {
            allEventsForOrder[indOrder.EventId] = [];
          }
          allEventsForOrder[indOrder.EventId].push(indOrder.OrderId);
        });
      });
      payload.Reservations = allEventsForOrder;
      payload.Reservations[orderEvent.EventId] = [orderEvent.OrderId];
      const objectId = this.$store.getters['auth/userName'];

      doRequest(
        (response) => {
          switch (response.data.Status) {
            case 'PartnerConfirmation':
              // Need to wait for partner's response
              console.log('Order needs to be confirmed by partner');
              this.paymentMsg = this.$translate(
                'ORDER_WAIT_FOR_PARTNER_CONFIRMATION'
              );
              this.successDialog = true;
              break;
            case 'Confirmed':
              // Order confirmed by payment method of DirectDebit or free of charge
              console.log('Order already been confirmed');
              if (
                response.data.PaymentMessage != null &&
                response.data.PaymentMessage.trim() !== ''
              ) {
                this.paymentMsg = response.data.PaymentMessage;
              } else {
                this.paymentMsg = this.$translate('ORDER_COMPLETED_DEFAULT');
              }
              this.successDialog = true;
              break;
            case 'WaitingForPayment':
              // Waiting for payment, redirect to mollie
              console.log('Order waiting for payment');
              if (
                response.data.PaymentUrl != null &&
                response.data.PaymentUrl.trim() !== ''
              ) {
                window.location.href = response.data.PaymentUrl;
              } else {
                window.EventBus.fire('activateSnackBar', {
                  text: this.$translate('BOOKING_FAIL'),
                  color: 'error',
                });
              }
              break;
            default:
              console.log('Unknown order status: ', response.data.Status);
              this.paymentMsg = this.$translate('ORDER_COMPLETED_DEFAULT');
              this.successDialog = true;
              break;
          }
        },
        (error) => {
          // this.noBatch = false;
          // this.loading = false;
          this.error = true;
          if (error.data.ErrorCode === 'E_PO_023') {
            window.EventBus.fire(
              'activateErrorDialog',
              this.$translate('E_PO_023_BOOKWITHRESERVATIONS'),
              false
            );
          } else if (error.data.ErrorCode === 'E_PO_011') {
            window.EventBus.fire(
              'activateErrorDialog',
              this.$translate('E_PO_011'),
              false,
              this.$translate('NO'),
              this.$translate('YES'),
              orderEvent,
              'continueAfterErrorBookingInBookingUserDetails'
            );
          } else {
            window.EventBus.fire('activateSnackBar', {
              text: this.$translate('BOOKING_FAIL'),
              color: 'error',
            });
          }
        },
        'bookWithReservations',
        objectId,
        payload,
        'POST',
        600000,
        false
      );
    },
    continueAfterErrorBookingInBookingUserDetails(option, data) {
      if (option === 2) {
        this.bookWithReservations(data, true);
      }
    },
    getUserInfo() {
      return {
        Name: `${this.user.firstName} ${this.user.lastName}` || '',
        Email: this.user.email || '',
        PhoneNumber: this.user.phoneNo || '',
        Street: this.user.street || '',
        Nr: this.user.houseNo || '',
        PostalCode: this.user.postalCode || '',
        City: this.user.city || '',
        Country: this.user.country || '',
        CountryCode: this.user.countryCode || '',
        Latitude: this.user.latitude == null ? -200 : this.user.latitude,
        Longitude: this.user.longitude == null ? -200 : this.user.longitude,
      };
    },
    isDeliveryAddressValid() {
      if (this.user.houseNo == null || this.user.houseNo.trim() === '') {
        return false;
      }
      if (this.user.street == null || this.user.street.trim() === '') {
        return false;
      }
      if (this.user.postalCode == null || this.user.postalCode.trim() === '') {
        return false;
      }
      if (this.user.city == null || this.user.city.trim() === '') {
        return false;
      }
      return true;
    },
    geocodeAddress() {
      // Get gps location based on user's address info if Delivery needed
      if (this.user.latitude == null) {
        this.user.latitude = -200;
      }
      if (this.user.longitude == null) {
        this.user.longitude = -200;
      }

      if (!this.isDeliveryAddressValid()) {
        return;
      }

      // Start geocoding
      const userAddressObj = this.getUserInfo();
      forwardGeocoding(
        userAddressObj,
        (result) => {
          // Geocoding finished successfully
          if (result.Latitude != null) {
            this.user.latitude = result.Latitude;
          }
          if (result.Longitude != null) {
            this.user.longitude = result.Longitude;
          }
        },
        () => {
          // Geocoding finished with error
        }
      );
    },
  },
};
</script>

<style scoped>
.subtotal {
  color: black !important;
  text-align: right;
}
.adressStyling {
  margin-bottom: 4px;
}
.buttons {
  width: 50%;
}
.error-text {
  color: red;
}
</style>
